// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// Portfolio Insights — estimate-vs-awarded accuracy across every project.
// The data-flywheel payoff: tells an estimator how accurate they actually
// are, by CSI division, from their own awarded buyout history. Loads each
// project's state, runs the pure engine in lib/insights, and presents it.

import React, { useState, useEffect, useMemo } from "react";
import { sGetRaw } from "../lib/scopedStorage";
import type { SupabaseStorageProvider } from "../lib/supabaseStorage";
import type { UserProfile } from "../types";
import { DARK_THEME, LIGHT_THEME } from "../lib/theme";
import { computePortfolioAccuracy, type PortfolioAccuracy } from "../lib/insights";
import { computePartnerScores, type PartnerScore } from "../lib/partnerScorecards";
import { computeCostObservations, groupObservationsByDivision, isRateUnit, type CostObservation } from "../lib/unitCostHistory";
import { computePortfolioFinancials, type PortfolioFinancials } from "../lib/portfolioFinancials";
import { buildDemoPortfolio, demoStorage } from "../lib/demoPortfolio";
import { ALL_DIVISIONS } from "../projectData";
import BenchmarkView from "./BenchmarkView";
import EmptyState from "./EmptyState";
import InfoTooltip from "./InfoTooltip";
import Button from "./Button";
import * as Icon from "./OEIcons";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";

function fmt(n: number) {
  const abs = Math.abs(Math.round(n));
  const s = "$" + abs.toLocaleString();
  return n < 0 ? `(${s})` : s;
}
function pctStr(n: number) {
  const v = n * 100;
  return `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;
}

interface Props {
  storage: SupabaseStorageProvider;
  user: UserProfile;
  onBack: () => void;
}

export default function InsightsView({ storage, user, onBack }: Props) {
  const [isDarkMode] = useState<boolean>(() => {
    try { const s = sGetRaw("oe_dark_mode"); return s === null ? true : s === "true"; }
    catch { return true; }
  });
  const T = isDarkMode ? DARK_THEME : LIGHT_THEME;

  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"portfolio" | "compare" | "accuracy" | "partners" | "library">("portfolio");
  const [accuracy, setAccuracy] = useState<PortfolioAccuracy | null>(null);
  const [partners, setPartners] = useState<PartnerScore[]>([]);
  const [observations, setObservations] = useState<CostObservation[]>([]);
  const [financials, setFinancials] = useState<PortfolioFinancials | null>(null);
  const [projectCount, setProjectCount] = useState(0);
  // Whose projects this summarises.
  //
  // The org is the default: the point of portfolio analysis is comparing
  // across the firm's history, and one estimator's own projects are the
  // narrower question. listProjectsAsync already returns everything RLS lets
  // you see -- your own, your org's and anything shared -- so "mine" is a
  // filter on that rather than a different query. sharedVia distinguishes
  // them, and filtering BEFORE the per-project load also saves the round
  // trips.
  const [scope, setScope] = useState<"org" | "mine">("org");
  const [hasOrgProjects, setHasOrgProjects] = useState(false);
  // Insights says nothing until the fifth or sixth project, which is exactly
  // the commitment a new estimator has not made yet — so the empty states
  // offer a worked example instead of a dead end.
  const [demo, setDemo] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        if (demo) {
          const sample = buildDemoPortfolio();
          setHasOrgProjects(false);
          setProjectCount(sample.length);
          setAccuracy(computePortfolioAccuracy(sample as any));
          setPartners(computePartnerScores(sample as any));
          setObservations(computeCostObservations(sample as any));
          setFinancials(computePortfolioFinancials(sample as any));
          setLoading(false);
          return;
        }
        const all = await storage.listProjectsAsync();
        if (cancelled) return;
        setHasOrgProjects(all.some((p: any) => p.sharedVia !== "owner"));
        const list = scope === "mine"
          ? all.filter((p: any) => p.sharedVia === "owner")
          : all;
        setProjectCount(list.length);
        // Load each project's full state (bid/buyout data lives in extra_state).
        const loaded = await Promise.all(
          list.map(async (p: any) => {
            const state = (await storage.loadProjectById(p.id)) as any;
            return {
              id: p.id,
              name: p.name,
              dateISO: p.savedAt || "",
              divisions: state?.divisions || [],
              itemState: state?.itemState || {},
              overhead: state?.overhead, profit: state?.profit, contingency: state?.contingency,
              buildingArea: state?.buildingArea,
              bids: state?.bids || [],
              bidPartners: state?.bidPartners || [],
              bidScopes: state?.bidScopes || [],
              buyoutItems: state?.buyoutItems || [],
            };
          }),
        );
        if (cancelled) return;
        setAccuracy(computePortfolioAccuracy(loaded));
        setPartners(computePartnerScores(loaded));
        setObservations(computeCostObservations(loaded));
        setFinancials(computePortfolioFinancials(loaded));
      } catch (e) {
        console.error("InsightsView: load failed", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [storage, scope, demo]);

  // No bias colouring and no verdict pill. The buyout log is filled in by
  // hand after the fact, so every division here is computed on a self-selected
  // sample -- and the rows people remember to enter are the ones that went
  // badly. Three scopes out of twenty, coloured red and labelled "estimate ran
  // low", is a judgement on someone's work drawn from the three jobs that
  // annoyed them. The variance is still shown, because it is arithmetic on
  // what was entered; the reading of it is not, because that requires knowing
  // how much is missing, and nobody can see that from here.

  return (
    <div style={{ fontFamily: FONT, background: T.bgApp, minHeight: "100vh", color: T.textSecondary }}>
      {/* The tab strip still scrolls when the tabs outrun a narrow window --
          it just does not put a grey trough under the navigation to say so.
          scrollbar-width covers current browsers, the webkit rule the rest. */}
      <style>{`
        .oe-noscrollbar { scrollbar-width: none; -ms-overflow-style: none; }
        .oe-noscrollbar::-webkit-scrollbar { width: 0; height: 0; display: none; }
      `}</style>
      {/* Header — bgHeader is dark navy in BOTH themes, so text must be the
          light textInverse, not textPrimary/textWeak (which go dark in light mode). */}
      <div style={{ background: T.bgHeader, borderBottom: `1px solid ${T.borderPrimary}`, padding: "0 24px", height: 56, display: "flex", alignItems: "center", gap: 14 }}>
        <Button T={T} variant="ghost" size="sm" icon={<Icon.ArrowLeft size={14} />} onClick={onBack} style={{ color: T.textInverse }}>Projects</Button>
        {/* Title over subtitle, the way a project header carries its name over
            its address. */}
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: T.textInverse, lineHeight: 1.15 }}>Portfolio Insights</div>
          <div style={{ fontSize: 11, color: `${T.textInverse}88` }}>
            {demo ? "Sample portfolio" : scope === "org" ? "Across every project you can see" : "Your own projects only"}
          </div>
        </div>
        {/* One right-aligned group: the scope switch, the way into (or out of)
            the sample portfolio, then the headline numbers. */}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {/* Scope is only worth offering once there is something to narrow:
              on a solo account the two views are the same list, and on sample
              data they are meaningless. */}
          {!demo && hasOrgProjects && (
            <div style={{ display: "flex", background: `${T.textInverse}14`, borderRadius: 3, overflow: "hidden" }}>
              {([["org", "Organization"], ["mine", "My projects"]] as const).map(([id, label]) => {
                const on = scope === id;
                return (
                  <button key={id} onClick={() => setScope(id)}
                    style={{ padding: "5px 12px", fontSize: 11, fontFamily: FONT, border: "none", cursor: "pointer",
                      fontWeight: on ? 700 : 400,
                      background: on ? T.borderAccent : "transparent",
                      color: on ? T.textOnGold : `${T.textInverse}99` }}>
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {demo ? (
            <button onClick={() => setDemo(false)}
              style={{ padding: "5px 12px", fontSize: 11, fontFamily: FONT, cursor: "pointer",
                background: T.borderAccent, color: T.textOnGold, border: "none", borderRadius: 3, fontWeight: 700 }}>
              Back to my data
            </button>
          ) : (
            // Reachable with a project or two already logged, not just from
            // the empty states. The estimator who most needs to see where
            // this ends up is the one whose portfolio is thin rather than
            // bare — and they never see an empty state.
            <button onClick={() => setDemo(true)}
              style={{ padding: "5px 10px", fontSize: 11, fontFamily: FONT, cursor: "pointer",
                background: "transparent", color: `${T.textInverse}99`, border: `1px solid ${T.textInverse}33`, borderRadius: 3 }}
              onMouseEnter={(e) => { e.currentTarget.style.color = T.textInverse; e.currentTarget.style.borderColor = `${T.textInverse}77`; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = `${T.textInverse}99`; e.currentTarget.style.borderColor = `${T.textInverse}33`; }}>
              View sample data
            </button>
          )}

          {/* The two numbers that answer "how big is this portfolio", carried
              in the header the way a project carries its total and $/SF —
              visible from every tab, not just the one that happens to roll
              them up. */}
          {financials && financials.projectCount > 0 && (
            <>
              <div style={{ width: 1, height: 28, background: `${T.textInverse}22`, marginLeft: 6 }} />
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 10, color: `${T.textInverse}88`, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 2 }}>Portfolio Value</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: T.borderAccent, fontVariantNumeric: "tabular-nums" }}>
                  {fmt(financials.totalContractValue)}
                </div>
              </div>
              <div style={{ width: 1, height: 28, background: `${T.textInverse}22` }} />
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 10, color: `${T.textInverse}88`, letterSpacing: ".14em", textTransform: "uppercase", marginBottom: 2 }}>Blended $/SF</div>
                <div style={{ fontSize: 14, fontWeight: 500, color: T.positive, fontVariantNumeric: "tabular-nums" }}>
                  {financials.blendedCostPerSF > 0 ? fmt(financials.blendedCostPerSF) : "—"}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Tab bar — the estimate's own sub-tab vocabulary: uppercase, tracked
          out, dimmed until selected, on a darkened strip under the header.
          Matching it matters more than any individual choice here, because a
          user arriving from a project should not feel they left the app. */}
      <div role="tablist" aria-label="Insights views" className="oe-noscrollbar"
        style={{ display: "flex", alignItems: "stretch", height: 34, padding: "0 24px",
          // The estimate darkens its sub-tab strip with 18% black, but that
          // overlay sits ON the navy header. Applied without the navy beneath
          // it, the same value tints the page background instead: a light grey
          // bar carrying light text, unreadable in light mode. Layer it over
          // bgHeader explicitly so the strip is dark in both themes, which is
          // what textInverse assumes.
          background: `linear-gradient(rgba(0,0,0,0.18), rgba(0,0,0,0.18)), ${T.bgHeader}`,
          borderBottom: `1px solid ${T.borderPrimary}`, overflowX: "auto" }}>
        {([{ id: "portfolio", label: "Portfolio" }, { id: "compare", label: "Compare Projects" }, { id: "accuracy", label: "Estimate Accuracy" }, { id: "partners", label: "Trade Partners" }, { id: "library", label: "Cost Library" }] as const).map((t) => {
          const active = tab === t.id;
          return (
            <button key={t.id} role="tab" aria-selected={active} onClick={() => setTab(t.id)}
              style={{ cursor: "pointer", padding: "0 14px", height: "100%", border: "none", background: "transparent",
                fontFamily: FONT, fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase",
                fontWeight: active ? 700 : 500, color: T.textInverse, whiteSpace: "nowrap",
                borderBottom: `2px solid ${active ? T.textInverse : "transparent"}`,
                // 0.4, copied from the estimate's sub-tabs, puts cream on this
                // strip at 3.2:1 — under the 4.5:1 small text needs. 0.55 is
                // 4.9:1 and still reads as clearly unselected next to the
                // active tab's full weight and underline.
                opacity: active ? 1 : 0.55, transition: "all .15s", display: "flex", alignItems: "center" }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.opacity = "0.8"; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.opacity = "0.55"; }}>
              {t.label}
            </button>
          );
        })}
      </div>

      {demo && (
        <div style={{ background: `${T.borderAccent}1a`, borderBottom: `1px solid ${T.borderAccent}55`,
          padding: "10px 24px", display: "flex", alignItems: "center", gap: 10, justifyContent: "center" }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase",
            color: T.borderAccent, border: `1px solid ${T.borderAccent}`, borderRadius: 3, padding: "2px 7px" }}>
            Sample data
          </span>
          <span style={{ fontSize: 12, color: T.textSecondary }}>
            Six example projects, so you can see what this looks like. Your own jobs replace it as you complete them.
          </span>
        </div>
      )}

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 24px" }}>
        {tab === "compare" ? (
          // Compare Projects loads its own data — don't gate it on the
          // portfolio aggregate load the other tabs depend on. On sample data
          // it gets a shim rather than the real provider: it asks storage
          // directly, so without this it shows the user's own projects under a
          // banner announcing sample data. Keyed so it remounts on the switch.
          <BenchmarkView key={demo ? "demo" : "real"} embedded
            storage={demo ? demoStorage() : storage} user={user} onBack={onBack} />
        ) : loading ? (
          <div style={{ padding: "60px 0", textAlign: "center", color: T.textWeak, fontSize: 13 }}>
            Analyzing {projectCount || ""} projects…
          </div>
        ) : tab === "portfolio" ? (
          !financials || financials.projectCount === 0 ? (
            <EmptyState T={T}
              icon={<Icon.BarChart size={32} />}
              headline="No projects to roll up yet"
              body="Once you have estimates with costs, this rolls up total contract value, blended margin, and cost per SF across your whole portfolio — and breaks it down project by project."
              ctaLabel="Show me with sample data"
              onCta={() => setDemo(true)}
            />
          ) : (
            <PortfolioTable T={T} f={financials} />
          )
        ) : tab === "partners" ? (
          partners.length === 0 ? (
            <EmptyState T={T}
              icon={<Icon.Users size={32} />}
              headline="No trade-partner history yet"
              body="As you collect bids and award packages, this builds a scorecard for each firm — how often they bid, how often they win, and how their awarded contracts land against your budget — so you know who to invite next time."
              ctaLabel="Show me with sample data"
              onCta={() => setDemo(true)}
            />
          ) : (
            <PartnerTable T={T} partners={partners} />
          )
        ) : tab === "library" ? (
          observations.length === 0 ? (
            <EmptyState T={T}
              icon={<Icon.FileText size={32} />}
              headline="No priced items yet"
              body="As you price line items across projects, this becomes a searchable history of what you've actually charged — every instance with its project, size, date, and cost basis — so you can pull up a real, comparable number when estimating the next job."
              ctaLabel="Show me with sample data"
              onCta={() => setDemo(true)}
            />
          ) : (
            <CostLibraryTable T={T} observations={observations} />
          )
        ) : !accuracy || accuracy.totalAwards === 0 ? (
          <EmptyState T={T}
            icon={<Icon.BarChart size={32} />}
            headline="No awarded buyouts to analyze yet"
            body="Once you award bids in the Buyout log (with a budget set on each), this page compares your estimates against the contracts you actually signed — by division, across every project — so you can see exactly where you run high or low."
            ctaLabel="Show me with sample data"
            onCta={() => setDemo(true)}
          />
        ) : (
          <>
            {/* Headline stats */}
            {/* Grid, not flex-wrap: with flex the widest card decided the track and
          a four-card row broke to three plus an orphan. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 14, marginBottom: 24 }}>
              <StatCard T={T} label="Awards Analyzed" value={String(accuracy.totalAwards)} />
              <StatCard T={T} label="Total Budgeted" value={fmt(accuracy.totalBudget)} />
              <StatCard T={T} label="Total Awarded" value={fmt(accuracy.totalContract)} />
              <StatCard T={T}
                label="Overall Variance"
                value={pctStr(accuracy.overallVariancePct)}
                hint="on the awards recorded below"
              />
            </div>

            {/* Per-division table */}
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 10 }}>
              Accuracy by Division
            </div>
            <div style={{ border: `1px solid ${T.borderPrimary}`, borderRadius: 10, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: T.bgPanel, color: T.textWeak, fontSize: 10, letterSpacing: ".06em", textTransform: "uppercase" }}>
                    <th style={{ textAlign: "left", padding: "10px 14px" }}>Division</th>
                    <th style={{ textAlign: "right", padding: "10px 14px" }}>Awards</th>
                    <th style={{ textAlign: "right", padding: "10px 14px" }}>Budgeted</th>
                    <th style={{ textAlign: "right", padding: "10px 14px" }}>Awarded</th>
                    <th style={{ textAlign: "right", padding: "10px 14px" }}>Variance</th>
                  </tr>
                </thead>
                <tbody>
                  {accuracy.divisions.map((d, i) => (
                    <tr key={d.divisionId} style={{ borderTop: `1px solid ${T.borderPrimary}`, background: i % 2 ? "transparent" : `${T.bgPanel}66` }}>
                      <td style={{ padding: "10px 14px", color: T.textSecondary }}>
                        <span style={{ color: T.textWeak, fontSize: 10, marginRight: 6 }}>{d.code}</span>
                        {d.name}
                      </td>
                      <td style={{ textAlign: "right", padding: "10px 14px", color: T.textTertiary, fontVariantNumeric: "tabular-nums" }}>{d.awardCount}</td>
                      <td style={{ textAlign: "right", padding: "10px 14px", color: T.textTertiary, fontVariantNumeric: "tabular-nums" }}>{fmt(d.totalBudget)}</td>
                      <td style={{ textAlign: "right", padding: "10px 14px", color: T.textTertiary, fontVariantNumeric: "tabular-nums" }}>{fmt(d.totalContract)}</td>
                      <td style={{ textAlign: "right", padding: "10px 14px", fontWeight: 700, fontVariantNumeric: "tabular-nums", color: T.textSecondary }}>
                        {pctStr(d.weightedVariancePct)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {accuracy.pairsWithoutBudget > 0 && (
              <div style={{ marginTop: 12, fontSize: 11, color: T.textWeak, fontStyle: "italic" }}>
                {accuracy.pairsWithoutBudget} awarded {accuracy.pairsWithoutBudget === 1 ? "row was" : "rows were"} skipped — no budget was set, so there's nothing to compare against. Set a budget on buyout rows to include them.
              </div>
            )}
            <div style={{ marginTop: 16, fontSize: 11, color: T.textWeak, lineHeight: 1.6 }}>
              <strong style={{ color: T.textTertiary }}>How to read this:</strong> variance is the awarded contract minus your budget,
              weighted by dollars, <em>on the rows you have entered</em>. Negative means those scopes were awarded for less than you budgeted;
              positive means they went over.
              <br /><br />
              It is not a measure of how you estimate. The buyout log is filled in by hand, and a scope you remembered to record
              is usually one that surprised you — so this reads high-variance by construction. Treat a division with two or three
              awards as two or three stories, not a pattern. It is worth something when a division is close to fully recorded,
              and worth very little before that.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Written from what portfolioFinancials actually computes, not from what the
// words usually mean. A tooltip that paraphrases its own label teaches nobody
// anything — what an estimator needs is the edge cases: what gets left out,
// and which of two plausible definitions this column uses.
const DEFS = {
  directCost: [
    "Every included line item at quantity x unit cost, before any markup.",
    "Leaves out rows you have switched off, voided items, and alternates that have not been accepted.",
  ].join("\n\n"),

  contractValue: [
    "Direct cost with this project's overhead, profit and contingency applied in sequence.",
    "Each percentage applies to the running total rather than to direct cost alone, so the three do not simply add up.",
  ].join("\n\n"),

  markup: [
    "Contract value minus direct cost: everything above the cost of the work itself.",
    "Overhead, profit and contingency combined.",
  ].join("\n\n"),

  margin: [
    "Markup divided by contract value, so the share of the contract that is not direct cost.",
    "Not the same as marking cost up by that percentage: a 25% markup on cost is a 20% margin.",
  ].join("\n\n"),

  costPerSF: [
    "Contract value divided by the building area recorded on the Project Info tab.",
    "A project with no area is left out of the blended figure rather than counted as zero square feet.",
  ].join("\n\n"),
};

function PortfolioTable({ T, f }: { T: any; f: PortfolioFinancials }) {
  const th: React.CSSProperties = { padding: "10px 14px", fontSize: 10, letterSpacing: ".06em", textTransform: "uppercase", color: T.textWeak };
  const td: React.CSSProperties = { padding: "10px 14px", fontVariantNumeric: "tabular-nums" };
  const money = (n: number) => "$" + Math.round(n).toLocaleString();
  const pct = (n: number) => `${(n * 100).toFixed(1)}%`;
  return (
    <>
      {/* Grid, not flex-wrap: with flex the widest card decided the track and
          a four-card row broke to three plus an orphan. */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 14, marginBottom: 24 }}>
        {/* Four, not six. Portfolio value and blended $/SF now sit in the
            header on every tab, and repeating them here left one card orphaned
            on a row of its own. Direct cost takes the slot instead, since it
            appears nowhere else. */}
        <StatCard T={T} label="Projects" value={String(f.projectCount)} />
        <StatCard T={T} label="Total Direct Cost" value={money(f.totalDirectCost)} info={DEFS.directCost} />
        <StatCard T={T} label="Total Markup" value={money(f.totalMarkup)} hint="overhead + profit + contingency" info={DEFS.markup} />
        <StatCard T={T} label="Blended Margin" value={pct(f.blendedMarginPct)} color={T.borderAccent} info={DEFS.margin} />
      </div>

      <div style={{ fontSize: 11, fontWeight: 700, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", marginBottom: 10 }}>
        By Project
      </div>
      <div style={{ border: `1px solid ${T.borderPrimary}`, borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: T.bgPanel }}>
              <th style={{ ...th, textAlign: "left" }}>Project</th>
              <ColHead T={T} label="Direct Cost" info={DEFS.directCost} th={th} />
              <ColHead T={T} label="Contract Value" info={DEFS.contractValue} th={th} />
              <ColHead T={T} label="Markup" info={DEFS.markup} th={th} />
              <ColHead T={T} label="Margin" info={DEFS.margin} th={th} />
              <ColHead T={T} label="$/SF" info={DEFS.costPerSF} th={th} />
            </tr>
          </thead>
          <tbody>
            {f.projects.map((p, i) => (
              <tr key={p.id} style={{ borderTop: `1px solid ${T.borderPrimary}`, background: i % 2 ? "transparent" : `${T.bgPanel}66` }}>
                <td style={{ ...td, textAlign: "left", color: T.textSecondary, fontWeight: 600 }}>{p.name}</td>
                <td style={{ ...td, textAlign: "right", color: T.textTertiary }}>{money(p.subtotal)}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 700, color: T.textPrimary }}>{money(p.total)}</td>
                <td style={{ ...td, textAlign: "right", color: T.textTertiary }}>{money(p.markup)}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 600, color: T.borderAccent }}>{pct(p.marginPct)}</td>
                <td style={{ ...td, textAlign: "right", color: T.textTertiary }}>{p.costPerSF > 0 ? money(p.costPerSF) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 12, fontSize: 11, color: T.textWeak, lineHeight: 1.6 }}>
        Hover any <strong style={{ color: T.textTertiary }}>?</strong> for what the column counts and what it leaves out.
      </div>
    </>
  );
}

function CostLibraryTable({ T, observations }: { T: any; observations: CostObservation[] }) {
  const [q, setQ] = useState("");
  const [ratesOnly, setRatesOnly] = useState(true);   // hide LS/Months/% lump sums by default
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const money = (n: number) => "$" + n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  const fmtDate = (iso: string) => {
    const t = Date.parse(iso);
    return isNaN(t) ? "—" : new Date(t).toLocaleDateString(undefined, { month: "short", year: "2-digit" });
  };
  const statusStyle = (s: string): { bg: string; fg: string } => {
    const k = (s || "").toLowerCase();
    if (k === "hard bid") return { bg: `${T.positive || "#68d391"}22`, fg: T.positive || "#68d391" };
    if (k === "allowance") return { bg: "#f6ad5522", fg: "#d4943b" };
    if (k === "needs review") return { bg: "#fc818122", fg: "#fc8181" };
    return { bg: `${T.textWeak}18`, fg: T.textWeak };
  };

  const ql = q.trim().toLowerCase();
  const filtered = observations.filter((o) =>
    (!ratesOnly || isRateUnit(o.unit)) &&
    (!ql || o.description.toLowerCase().includes(ql)),
  );
  const groups = groupObservationsByDivision(filtered);
  const totalShown = filtered.length;

  const th: React.CSSProperties = { padding: "7px 14px", fontSize: 10, letterSpacing: ".06em", textTransform: "uppercase", color: T.textWeak };
  const td: React.CSSProperties = { padding: "8px 14px", fontVariantNumeric: "tabular-nums" };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search an item — e.g. slab, framing, conduit…"
          style={{ fontSize: 13, padding: "8px 12px", flex: "1 1 320px", background: T.bgInput, border: `1px solid ${T.borderSecondary}`, borderRadius: 6, color: T.textPrimary, fontFamily: FONT, outline: "none" }} />
        <button onClick={() => setRatesOnly((v) => !v)}
          title="Hide lump-sum, allowance, %, and duration (Months/Weeks/Days) items — they're project-specific, not reusable rates"
          style={{ fontSize: 11, padding: "7px 12px", borderRadius: 10, cursor: "pointer", fontFamily: FONT, fontWeight: 600,
            border: `1px solid ${ratesOnly ? T.borderAccent : T.borderSecondary}`,
            background: ratesOnly ? `${T.borderAccent}1e` : "transparent", color: ratesOnly ? T.borderAccent : T.textWeak }}>
          {ratesOnly ? "✓ " : ""}Unit rates only
        </button>
      </div>

      {totalShown === 0 ? (
        <div style={{ padding: "32px 0", textAlign: "center", color: T.textWeak, fontSize: 12 }}>
          No matches. {ratesOnly && "Try turning off “Unit rates only,” or "}refine your search.
        </div>
      ) : (
        groups.map((g) => {
          const isCollapsed = collapsed[g.divisionId];
          return (
            <div key={g.divisionId} style={{ marginBottom: 14, border: `1px solid ${T.borderPrimary}`, borderRadius: 10, overflow: "hidden" }}>
              <button onClick={() => setCollapsed((c) => ({ ...c, [g.divisionId]: !c[g.divisionId] }))}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, padding: "9px 14px", background: T.bgPanel, border: "none", cursor: "pointer", fontFamily: FONT, textAlign: "left" }}>
                <span style={{ fontSize: 10, color: T.textWeak, transition: "transform .15s", transform: isCollapsed ? "none" : "rotate(90deg)" }}>▶</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: T.textWeak, letterSpacing: ".06em" }}>{g.divisionCode}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>{g.divisionName}</span>
                <span style={{ fontSize: 11, color: T.textWeak, marginLeft: "auto" }}>{g.observations.length} {g.observations.length !== 1 ? "entries" : "entry"}</span>
              </button>
              {!isCollapsed && (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderTop: `1px solid ${T.borderPrimary}` }}>
                      <th style={{ ...th, textAlign: "left" }}>Item</th>
                      <th style={{ ...th, textAlign: "center" }}>Unit</th>
                      <th style={{ ...th, textAlign: "right" }}>Unit Cost</th>
                      <th style={{ ...th, textAlign: "right" }}>Qty</th>
                      <th style={{ ...th, textAlign: "left" }}>Basis</th>
                      <th style={{ ...th, textAlign: "left" }}>Project</th>
                      <th style={{ ...th, textAlign: "right" }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.observations.map((o, i) => {
                      const ss = statusStyle(o.costStatus);
                      return (
                        <tr key={`${o.projectId}|${o.description}|${o.unit}|${i}`} style={{ borderTop: `1px solid ${T.borderPrimary}`, background: i % 2 ? "transparent" : `${T.bgPanel}66` }}>
                          <td style={{ ...td, textAlign: "left", color: T.textSecondary }}>{o.description}</td>
                          <td style={{ ...td, textAlign: "center", color: T.textWeak }}>{o.unit}</td>
                          <td style={{ ...td, textAlign: "right", fontWeight: 700, color: T.textPrimary }}>{money(o.unitCost)}</td>
                          <td style={{ ...td, textAlign: "right", color: T.textWeak }}>{o.qty ? o.qty.toLocaleString() : "—"}</td>
                          <td style={{ ...td, textAlign: "left" }}>
                            {o.costStatus
                              ? <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 10, background: ss.bg, color: ss.fg }}>{o.costStatus}</span>
                              : <span style={{ color: T.textWeak }}>—</span>}
                          </td>
                          <td style={{ ...td, textAlign: "left", color: T.textTertiary }}>
                            {o.projectName}
                            {o.projectArea > 0 && <span style={{ color: T.textWeak, fontSize: 10 }}> · {o.projectArea.toLocaleString()} SF</span>}
                          </td>
                          <td style={{ ...td, textAlign: "right", color: T.textWeak }}>{fmtDate(o.dateISO)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          );
        })
      )}

      <div style={{ marginTop: 8, fontSize: 11, color: T.textWeak, lineHeight: 1.6 }}>
        Every price you've actually entered, shown in context — no blended averages, because costs only compare when the scope matches.
        Use <strong style={{ color: T.textTertiary }}>Basis</strong> and project size to judge which past number is comparable.
        Showing {totalShown} entr{totalShown !== 1 ? "ies" : "y"}.
      </div>
    </>
  );
}

const PARTNER_SORTS = {
  name:         (p: PartnerScore) => p.name.toLowerCase(),
  packageCount: (p: PartnerScore) => p.packageCount,
  winCount:     (p: PartnerScore) => p.winCount,
  winRate:      (p: PartnerScore) => p.winRate,
  // Null is "no record yet", not zero. It sorts to the bottom either way
  // rather than masquerading as the best or the worst performer.
  avgVariancePct: (p: PartnerScore) => p.avgVariancePct,
  projectCount: (p: PartnerScore) => p.projectCount,
  lastBidDaysAgo: (p: PartnerScore) => p.lastBidDaysAgo,
} as const;

type PartnerSortKey = keyof typeof PARTNER_SORTS;

const AVG_VS_BUDGET_DEF = [
  "Across the packages this firm WON, the average of (their contract amount minus your budget for that scope) divided by your budget.",
  "Negative means their contracts landed under the budget you set.",
  "It measures your estimating as much as their pricing: if you habitually over-budget concrete, every concrete sub will read negative.",
  "Losing bids are ignored, and so is any awarded package whose buyout row has no budget on it. n = how many wins the average is built from.",
].join("\n\n");

function SortTh({ T, th, k, label, align = "right", info, sort, onSort }: {
  T: any; th: React.CSSProperties; k: PartnerSortKey; label: string;
  align?: "left" | "right"; info?: string;
  sort: { key: PartnerSortKey; dir: "asc" | "desc" };
  onSort: (k: PartnerSortKey) => void;
}) {
  const on = sort.key === k;
  return (
    <th
      style={{ ...th, textAlign: align, cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
      onClick={() => onSort(k)}
      aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: on ? T.textPrimary : undefined }}>
        {/* Label then marker then arrow, matching the Portfolio table's
            column heads rather than inventing a second order. */}
        {label}
        {info && <InfoTooltip text={info} T={T} FONT={FONT} size={10} />}
        <span style={{ opacity: on ? 1 : 0.25, fontSize: 10 }}>{on && sort.dir === "asc" ? "▲" : "▼"}</span>
      </span>
    </th>
  );
}

function PartnerTable({ T, partners }: { T: any; partners: PartnerScore[] }) {
  const [q, setQ] = useState("");
  const [divFilter, setDivFilter] = useState("all");
  const [sort, setSort] = useState<{ key: PartnerSortKey; dir: "asc" | "desc" }>({
    key: "packageCount", dir: "desc",
  });

  const th: React.CSSProperties = { padding: "10px 14px", fontSize: 10, letterSpacing: ".06em", textTransform: "uppercase", color: T.textWeak };
  const td: React.CSSProperties = { padding: "10px 14px", fontVariantNumeric: "tabular-nums" };
  const varColor = (v: number | null) =>
    v == null ? T.textWeak : v < -0.02 ? "#f6ad55" : v > 0.02 ? "#fc8181" : (T.positive || "#68d391");
  const recency = (d: number | null) =>
    d == null ? "—" : d === 0 ? "today" : d < 30 ? `${d}d ago` : d < 365 ? `${Math.round(d / 30)}mo ago` : `${Math.round(d / 365)}y ago`;

  // Only the divisions somebody has actually bid in. A dropdown listing all 24
  // CSI divisions when the firm works in nine is a worse list than no list.
  const divisions = useMemo(() => {
    const present = new Set<string>();
    for (const p of partners) for (const d of p.divisionIds) present.add(d);
    return ALL_DIVISIONS.filter((d) => present.has(d.id))
      .map((d) => ({ id: d.id, label: `${d.code} — ${d.name}` }));
  }, [partners]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = partners.filter((p) => {
      if (divFilter !== "all" && !p.divisionIds.includes(divFilter)) return false;
      if (!needle) return true;
      return p.name.toLowerCase().includes(needle) || (p.trade || "").toLowerCase().includes(needle);
    });

    const get = PARTNER_SORTS[sort.key];
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      const av = get(a) as string | number | null;
      const bv = get(b) as string | number | null;
      // Missing values stay at the bottom whichever way the column is sorted.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string" || typeof bv === "string") {
        return String(av).localeCompare(String(bv)) * dir;
      }
      return (av - bv) * dir;
    });
  }, [partners, q, divFilter, sort]);

  const toggleSort = (key: PartnerSortKey) =>
    setSort((s) => (s.key === key
      ? { key, dir: s.dir === "asc" ? "desc" : "asc" }
      // A fresh column opens the way you would want to read it: names A to Z,
      // recency newest first, every other column biggest first.
      : { key, dir: key === "name" || key === "lastBidDaysAgo" ? "asc" : "desc" }));

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase" }}>
          Trade Partner Scorecards
        </div>
        <div style={{ flex: 1 }} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search a firm or trade"
          style={{ background: T.bgInput, color: T.textPrimary, border: `1px solid ${T.borderSecondary}`,
            borderRadius: 3, padding: "6px 10px", fontSize: 12, fontFamily: FONT, minWidth: 210, outline: "none" }}
        />
        <select
          value={divFilter}
          onChange={(e) => setDivFilter(e.target.value)}
          style={{ background: T.bgInput, color: T.textPrimary, border: `1px solid ${T.borderSecondary}`,
            borderRadius: 3, padding: "6px 10px", fontSize: 12, fontFamily: FONT, cursor: "pointer", outline: "none" }}
        >
          <option value="all">All divisions</option>
          {divisions.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
        </select>
      </div>

      <div style={{ border: `1px solid ${T.borderPrimary}`, borderRadius: 10, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: T.bgPanel }}>
              <SortTh T={T} th={th} sort={sort} onSort={toggleSort} k="name" label="Partner" align="left" />
              <SortTh T={T} th={th} sort={sort} onSort={toggleSort} k="packageCount" label="Packages Bid" />
              <SortTh T={T} th={th} sort={sort} onSort={toggleSort} k="winCount" label="Won" />
              <SortTh T={T} th={th} sort={sort} onSort={toggleSort} k="winRate" label="Win Rate" />
              <SortTh T={T} th={th} sort={sort} onSort={toggleSort} k="avgVariancePct" label="Avg vs Budget" info={AVG_VS_BUDGET_DEF} />
              <SortTh T={T} th={th} sort={sort} onSort={toggleSort} k="projectCount" label="Projects" />
              <SortTh T={T} th={th} sort={sort} onSort={toggleSort} k="lastBidDaysAgo" label="Last Bid" />
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ ...td, textAlign: "center", color: T.textWeak, padding: "28px 14px" }}>
                  No partner matches that search.
                </td>
              </tr>
            ) : shown.map((p, i) => (
              <tr key={p.name} style={{ borderTop: `1px solid ${T.borderPrimary}`, background: i % 2 ? "transparent" : `${T.bgPanel}66` }}>
                <td style={{ ...td, textAlign: "left", color: T.textSecondary }}>
                  <div style={{ fontWeight: 600 }}>{p.name}</div>
                  {p.trade && <div style={{ fontSize: 10, color: T.textWeak }}>{p.trade}</div>}
                </td>
                <td style={{ ...td, textAlign: "right", color: T.textTertiary }}>{p.packageCount}</td>
                <td style={{ ...td, textAlign: "right", color: T.textTertiary }}>{p.winCount}</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 700, color: T.textPrimary }}>{Math.round(p.winRate * 100)}%</td>
                <td style={{ ...td, textAlign: "right", fontWeight: 700, color: varColor(p.avgVariancePct) }}>
                  {p.avgVariancePct == null ? "—" : `${p.avgVariancePct > 0 ? "+" : ""}${(p.avgVariancePct * 100).toFixed(1)}%`}
                  {/* One win is an anecdote. Say so, rather than letting a
                      single result read like a track record. */}
                  {p.varianceSamples > 0 && (
                    <span style={{ fontSize: 10, color: T.textWeak, fontWeight: 400, marginLeft: 5 }}>
                      n={p.varianceSamples}
                    </span>
                  )}
                </td>
                <td style={{ ...td, textAlign: "right", color: T.textTertiary }}>{p.projectCount}</td>
                <td style={{ ...td, textAlign: "right", color: T.textWeak }}>{recency(p.lastBidDaysAgo)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: T.textWeak, lineHeight: 1.6 }}>
        Showing {shown.length} of {partners.length} {partners.length === 1 ? "firm" : "firms"}. Amber on{" "}
        <strong style={{ color: T.textTertiary }}>Avg vs Budget</strong> means their contracts came in under your budget, red means over
        {" "}&mdash; hover the column for exactly what it counts.
      </div>
    </>
  );
}

/** A right-aligned numeric column header with its definition attached. */
function ColHead({ T, label, info, th }: { T: any; label: string; info: string; th: React.CSSProperties }) {
  return (
    <th style={{ ...th, textAlign: "right" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
        {label}
        <InfoTooltip text={info} T={T} FONT={FONT} size={10} />
      </span>
    </th>
  );
}

function StatCard({ T, label, value, color, hint, info }: { T: any; label: string; value: string; color?: string; hint?: string; info?: string }) {
  return (
    <div style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase", fontWeight: 700, marginBottom: 6, display: "flex", alignItems: "center", gap: 5 }}>
        {label}
        {info && <InfoTooltip text={info} T={T} FONT={FONT} size={10} />}
      </div>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || T.textPrimary, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {hint && <div style={{ fontSize: 10, color: T.textWeak, marginTop: 3 }}>{hint}</div>}
    </div>
  );
}
